import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { fromMinorUnits, formatMoney, updateCustomerSchema } from '@lombok/shared';
import type { Customer, UpdateCustomerInput } from '@lombok/shared';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@lombok/ui';
import { PageHeader } from '@/components/PageHeader';
import { BackLink, DetailRow } from '@/components/DetailRow';
import { AccountName } from '@/components/AccountName';
import { FORM_COLUMN } from '@/components/layout';
import { useIsAdmin } from '@/auth/useIsAdmin';
import { StatusPill } from '@/components/StatusPill';
import { QueryState } from '@/components/QueryState';
import {
  CheckboxField,
  FormActions,
  FormError,
  SelectField,
  TextField,
  emptyToUndefined,
} from '@/components/form';
import { PAYMENT_TERMS_OPTIONS, TERMS_LABELS_ES } from '@/features/admin/labels';
import { AccountStatusPanel } from '@/features/accounts/AccountStatusPanel';
import { useChangeCustomerStatus, useCustomer, useUpdateCustomer } from '@/data/hooks';
import { decimalOrUndefined } from '@/lib/decimal';
import { placeLabel } from '@/lib/names';
import { DocumentsForAccount } from '@/features/documents/DocumentosPage';
import { DistributorField } from './DistributorField';


/**
 * Cliente detail (admin) with inline edit. Read mode prominently surfaces
 * balance + available credit (a docs/11 priority) alongside account info;
 * "Editar" swaps to an RHF form validated by the shared `updateCustomerSchema`.
 * Balance/credit are derived server-side and shown read-only.
 */
export function CustomerDetailPage({ basePath = '/app/admin/clientes' }: { basePath?: string } = {}) {
  const BASE = basePath;
  const { id } = useParams<{ id: string }>();
  const query = useCustomer(id);
  const isAdmin = useIsAdmin();
  const [editing, setEditing] = useState(false);

  return (
    <div className={`flex flex-col gap-6 ${FORM_COLUMN}`}>
      <div className="flex flex-col gap-2">
        <BackLink to={BASE}>Clientes</BackLink>
        <PageHeader
          title={query.data?.name ?? 'Cliente'}
          description="Cuenta, crédito y saldo."
          actions={
            query.data && !editing && isAdmin ? (
              <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                Editar
              </Button>
            ) : undefined
          }
        />
      </div>

      <QueryState
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        onRetry={() => void query.refetch()}
        loadingLabel="Cargando cliente…"
      >
        {query.data ? (
          <div className="flex flex-col gap-6">
            <CreditSummary customer={query.data} />
            <CustomerStatus customer={query.data} />
            {editing ? (
              <CustomerEditForm customer={query.data} onDone={() => setEditing(false)} />
            ) : (
              <CustomerSummary customer={query.data} />
            )}
            <section className="flex flex-col gap-3">
              <h2 className="text-base font-semibold text-text-primary">Documentos</h2>
              <DocumentsForAccount accountId={query.data.customerId} listTitle="Documentos del cliente" emptyDescription="Este cliente aún no tiene documentos. Usa el formulario para cargar el primero." />
            </section>
          </div>
        ) : null}
      </QueryState>
    </div>
  );
}

function CustomerStatus({ customer: c }: { customer: Customer }) {
  const change = useChangeCustomerStatus(c.customerId);
  return (
    <AccountStatusPanel
      accountId={c.customerId}
      status={c.status}
      statusReason={c.statusReason}
      onChange={(input) => change.mutateAsync(input)}
    />
  );
}

/** Balance + available credit KPIs (always visible, never editable here). */
export function CreditSummary({ customer: c }: { customer: Customer }) {
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <Card>
        <CardContent className="pt-5">
          <p className="text-sm text-text-muted">Saldo pendiente</p>
          <p className="mt-1 text-2xl font-semibold tracking-tight text-text-primary">
            {formatMoney(c.openBalanceMinorUsd)}
          </p>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-5">
          <p className="text-sm text-text-muted">Crédito disponible</p>
          <p className="mt-1 text-2xl font-semibold tracking-tight text-success">
            {formatMoney(c.availableCreditMinorUsd)}
          </p>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-5">
          <p className="text-sm text-text-muted">Límite de crédito</p>
          <p className="mt-1 text-2xl font-semibold tracking-tight text-text-primary">
            {formatMoney(c.creditLimitMinorUsd)}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function CustomerSummary({ customer: c }: { customer: Customer }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Información de la cuenta</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <dl className="flex flex-col">
          <DetailRow label="Nombre">{c.name}</DetailRow>
          <DetailRow label="RIF">{c.rif}</DetailRow>
          <DetailRow label="Estado">
            <StatusPill kind="account" value={c.status} />
          </DetailRow>
          <DetailRow label="Términos de pago">{TERMS_LABELS_ES[c.paymentTerms]}</DetailRow>
          <DetailRow label="Lista de precios">{c.priceListId ?? '—'}</DetailRow>
          <DetailRow label="Vendedor asignado">{c.assignedSalesRepId ?? '—'}</DetailRow>
          <DetailRow label="Territorio">{placeLabel(c.territoryId)}</DetailRow>
          <DetailRow label="Distribuidor">{c.distributorId ? <AccountName id={c.distributorId} /> : '—'}</DetailRow>
          <DetailRow label="Promociones">{c.optInPromotions ? 'Sí' : 'No'}</DetailRow>
        </dl>
      </CardContent>
    </Card>
  );
}

function CustomerEditForm({ customer: c, onDone }: { customer: Customer; onDone: () => void }) {
  const mutation = useUpdateCustomer(c.customerId);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<UpdateCustomerInput>({
    resolver: zodResolver(updateCustomerSchema),
    defaultValues: toFormValues(c),
  });

  useEffect(() => {
    reset(toFormValues(c));
  }, [c, reset]);

  const onSubmit = handleSubmit((values) => {
    mutation.mutate(values, { onSuccess: () => onDone() });
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Editar cuenta</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <form onSubmit={onSubmit} className="flex flex-col gap-5" noValidate>
          <FormError error={mutation.error} />

          <TextField label="Nombre" required error={errors.name?.message} {...register('name')} />
          <TextField label="RIF" required error={errors.rif?.message} {...register('rif')} />

          <div className="grid gap-5 sm:grid-cols-2">
            <SelectField
              label="Términos de pago"
              options={PAYMENT_TERMS_OPTIONS}
              error={errors.paymentTerms?.message}
              {...register('paymentTerms')}
            />
            <TextField
              label="Límite de crédito (USD)"
              inputMode="decimal"
              placeholder="Ej. 5000,00"
              hint="Monto en dólares. El sistema lo guarda en centavos."
              error={errors.creditLimitUsd?.message}
              {...register('creditLimitUsd', { setValueAs: decimalOrUndefined })}
            />
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <TextField
              label="ID de lista de precios"
              placeholder="Opcional"
              error={errors.priceListId?.message}
              {...register('priceListId', { setValueAs: emptyToUndefined })}
            />
            <TextField
              label="ID de vendedor asignado"
              placeholder="Opcional"
              error={errors.assignedSalesRepId?.message}
              {...register('assignedSalesRepId', { setValueAs: emptyToUndefined })}
            />
          </div>
          <TextField
            label="Territorio"
            placeholder="Opcional"
            error={errors.territoryId?.message}
            {...register('territoryId', { setValueAs: emptyToUndefined })}
          />
          <DistributorField
            value={watch('distributorId') ?? ''}
            // "Sin distribuidor" removes an existing link (null); on an unlinked customer it changes nothing.
            onChange={(id) => setValue('distributorId', id || (c.distributorId ? null : undefined), { shouldDirty: true })}
          />

          <CheckboxField label="Acepta promociones" {...register('optInPromotions')} />

          <FormActions>
            <Button type="button" variant="outline" onClick={onDone}>
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending || !isDirty}>
              {mutation.isPending ? 'Guardando…' : 'Guardar cambios'}
            </Button>
          </FormActions>
        </form>
      </CardContent>
    </Card>
  );
}

/** Map a Customer to editable form values (credit limit shown in whole USD). */
function toFormValues(c: Customer): UpdateCustomerInput {
  return {
    name: c.name,
    rif: c.rif,
    paymentTerms: c.paymentTerms,
    creditLimitUsd: fromMinorUnits(c.creditLimitMinorUsd, 'USD'),
    priceListId: c.priceListId,
    assignedSalesRepId: c.assignedSalesRepId,
    territoryId: c.territoryId,
    distributorId: c.distributorId,
    optInPromotions: c.optInPromotions,
  };
}
